import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, unref as _unref, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, createCommentVNode as _createCommentVNode, createBlock as _createBlock, normalizeClass as _normalizeClass, createVNode as _createVNode } from "vue"

const _hoisted_1 = { class: "library-table-container" }
const _hoisted_2 = { class: "library-table-actions level mb-10" }
const _hoisted_3 = { class: "level-left" }
const _hoisted_4 = { class: "dropdown is-right is-fullwidth-mobile mr-5 mr-0-mobile js-no-close-on-click" }
const _hoisted_5 = { class: "dropdown-trigger is-fullwidth-mobile" }
const _hoisted_6 = {
  class: "button is-fullwidth-mobile",
  "aria-haspopup": "true",
  "aria-controls": "dropdown-menu"
}
const _hoisted_7 = ["innerHTML"]
const _hoisted_8 = {
  class: "dropdown-menu is-fullwidth-mobile",
  role: "menu"
}
const _hoisted_9 = { class: "dropdown-content" }
const _hoisted_10 = ["onClick"]
const _hoisted_11 = ["checked"]
const _hoisted_12 = ["onClick"]
const _hoisted_13 = {
  key: 1,
  class: "ml-5"
}
const _hoisted_14 = { key: 1 }
const _hoisted_15 = ["colspan"]
const _hoisted_16 = { key: 0 }
const _hoisted_17 = {
  key: 0,
  class: "has-text-grey mt-10"
}

import Rails from "@rails/ujs";
import { ref, computed, onMounted, watch, h } from "vue";
import {
  useVueTable,
  getCoreRowModel,
  getSortedRowModel,
  FlexRender,
  createColumnHelper,
  type SortingState,
  type ColumnDef,
  type RowSelectionState,
  type VisibilityState
} from "@tanstack/vue-table";

interface GamePurchaseRow {
  id: number;
  url: string;
  game_url: string;
  game: { id: number; name: string };
  rating: number | null;
  hours_played: string | null;
  completion_status: { label: string; value: string } | null;
  replay_count: number | null;
  start_date: string | null;
  completion_date: string | null;
  platforms: Array<{ id: number; name: string }>;
  stores: Array<{ id: number; name: string }>;
  comments: string | null;
}

interface Props {
  isEditable: boolean;
  rows: GamePurchaseRow[];
  isLoading: boolean;
  chevronDownIcon: string;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'library-table',
  props: {
    isEditable: { type: Boolean },
    rows: {},
    isLoading: { type: Boolean },
    chevronDownIcon: {}
  },
  emits: ["edit", "delete", "addGame", "selectedGamePurchasesChanged"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;

// Reactive state
const sorting = ref<SortingState>([]);
const rowSelection = ref<RowSelectionState>({});
const columnVisibility = ref<VisibilityState>({});

// Dark mode detection
const isDarkMode = computed(() => {
  return window.matchMedia("(prefers-color-scheme: dark)").matches;
});

// Helper functions
function formatHoursPlayed(value: string | null): string {
  if (value === null || parseFloat(value) === 0 || !/^\d+\.\d$/.test(value)) {
    return "";
  }

  const [hours, numMinutes] = value.split(".").map((s) => parseInt(s)) as [number, number];
  const minutes = Math.floor((numMinutes / 10) * 60);

  if (minutes === 0) {
    return `${hours}h`;
  } else if (hours === 0) {
    return `${minutes}m`;
  } else {
    return `${hours}h${minutes}m`;
  }
}

function formatDate(value: string | null): string {
  if (!value) return "";
  const date = new Date(value);
  return date.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
}

// Column definitions
const columnHelper = createColumnHelper<GamePurchaseRow>();

const baseColumns: ColumnDef<GamePurchaseRow, any>[] = [
  // Checkbox column for selection (if editable)
  ...(props.isEditable
    ? [
        columnHelper.display({
          id: "select",
          header: ({ table }) =>
            h("input", {
              type: "checkbox",
              checked: table.getIsAllRowsSelected(),
              indeterminate: table.getIsSomeRowsSelected(),
              onChange: table.getToggleAllRowsSelectedHandler()
            }),
          cell: ({ row }) =>
            h("input", {
              type: "checkbox",
              checked: row.getIsSelected(),
              disabled: !row.getCanSelect(),
              onChange: row.getToggleSelectedHandler()
            }),
          enableSorting: false,
          enableHiding: false
        })
      ]
    : []),

  columnHelper.accessor((row) => row.game.name, {
    id: "game.name",
    header: "Name",
    cell: (info) => h("a", { href: info.row.original.game_url }, info.getValue()),
    enableHiding: false
  }),

  columnHelper.accessor("rating", {
    header: "Rating",
    cell: (info) => info.getValue() ?? ""
  }),

  columnHelper.accessor("hours_played", {
    header: "Hours Played",
    cell: (info) => formatHoursPlayed(info.getValue())
  }),

  columnHelper.accessor((row) => row.completion_status?.label ?? "", {
    id: "completion_status.label",
    header: "Completion Status",
    cell: (info) => info.getValue()
  }),

  columnHelper.accessor("replay_count", {
    header: "Replay Count",
    cell: (info) => info.getValue() ?? ""
  }),

  columnHelper.accessor("start_date", {
    header: "Start Date",
    cell: (info) => formatDate(info.getValue())
  }),

  columnHelper.accessor("completion_date", {
    header: "Completion Date",
    cell: (info) => formatDate(info.getValue())
  }),

  columnHelper.accessor("platforms", {
    header: "Platforms",
    cell: (info) => {
      const platforms = info.getValue();
      if (!platforms || platforms.length === 0) return "";
      return h(
        "div",
        platforms.map((p: { id: number; name: string }) =>
          h("p", { key: p.id }, h("a", { href: `/platforms/${p.id}` }, p.name))
        )
      );
    },
    sortingFn: (rowA, rowB) => {
      const a = rowA.original.platforms;
      const b = rowB.original.platforms;
      if (a.length === 0 && b.length === 0) return 0;
      if (b.length === 0) return 1;
      if (a.length === 0) return -1;
      return a[0]!.name.toLowerCase().localeCompare(b[0]!.name.toLowerCase());
    }
  }),

  columnHelper.accessor("stores", {
    header: "Stores",
    cell: (info) => {
      const stores = info.getValue();
      if (!stores || stores.length === 0) return "";
      return h(
        "div",
        stores.map((s: { id: number; name: string }) =>
          h("p", { key: s.id }, h("a", { href: `/stores/${s.id}` }, s.name))
        )
      );
    },
    sortingFn: (rowA, rowB) => {
      const a = rowA.original.stores;
      const b = rowB.original.stores;
      if (a.length === 0 && b.length === 0) return 0;
      if (b.length === 0) return 1;
      if (a.length === 0) return -1;
      return a[0]!.name.toLowerCase().localeCompare(b[0]!.name.toLowerCase());
    }
  }),

  columnHelper.accessor("comments", {
    header: "Comments",
    cell: (info) => info.getValue() ?? ""
  }),

  // Actions column (if editable)
  ...(props.isEditable
    ? [
        columnHelper.display({
          id: "actions",
          header: "Actions",
          cell: ({ row }) =>
            h("span", [
              h("a", { class: "mr-5", onClick: () => onEdit(row.original) }, "Edit"),
              h("a", { class: "has-text-danger", onClick: () => onDelete(row.original) }, "Remove")
            ]),
          enableSorting: false,
          enableHiding: false
        })
      ]
    : [])
];

// Initialize table
const table = useVueTable({
  get data() {
    return props.rows;
  },
  columns: baseColumns,
  state: {
    get sorting() {
      return sorting.value;
    },
    get rowSelection() {
      return rowSelection.value;
    },
    get columnVisibility() {
      return columnVisibility.value;
    }
  },
  onSortingChange: (updaterOrValue) => {
    sorting.value =
      typeof updaterOrValue === "function" ? updaterOrValue(sorting.value) : updaterOrValue;
    // Save to localStorage
    if (sorting.value[0]) {
      localStorage.setItem("vglist:librarySortColumn", sorting.value[0].id);
      localStorage.setItem("vglist:librarySortDirection", sorting.value[0].desc ? "desc" : "asc");
    }
  },
  onRowSelectionChange: (updaterOrValue) => {
    rowSelection.value =
      typeof updaterOrValue === "function" ? updaterOrValue(rowSelection.value) : updaterOrValue;
    emit(
      "selectedGamePurchasesChanged",
      table.getSelectedRowModel().rows.map((r) => r.original)
    );
  },
  onColumnVisibilityChange: (updaterOrValue) => {
    columnVisibility.value =
      typeof updaterOrValue === "function"
        ? updaterOrValue(columnVisibility.value)
        : updaterOrValue;
    localStorage.setItem("vglist:libraryColumns", JSON.stringify(columnVisibility.value));
  },
  getCoreRowModel: getCoreRowModel(),
  getSortedRowModel: getSortedRowModel(),
  enableRowSelection: props.isEditable
});

// Methods
function onEdit(row: GamePurchaseRow) {
  emit("edit", row);
}

function onDelete(row: GamePurchaseRow) {
  if (window.confirm(`Remove ${row.game.name} from your library?`)) {
    const headers: HeadersInit = {
      Accept: "application/json",
      "X-CSRF-Token": Rails.csrfToken()!
    };

    fetch(row.url, {
      method: "DELETE",
      headers,
      credentials: "same-origin"
    }).then((response) => {
      if (response.ok) {
        emit("delete");
      }
    });
  }
}

function addGame() {
  emit("addGame");
}

// Lifecycle
onMounted(() => {
  // Initialize column visibility from localStorage
  const libraryColumnsData = localStorage.getItem("vglist:libraryColumns");
  if (libraryColumnsData !== null) {
    const libraryColumns = JSON.parse(libraryColumnsData);
    columnVisibility.value = libraryColumns;
  } else {
    // Set default visibility (hide some columns by default)
    columnVisibility.value = {
      replay_count: false,
      start_date: false,
      completion_date: false,
      platforms: false,
      stores: false
    };
  }

  // Initialize sorting from localStorage
  const sortColumnData = localStorage.getItem("vglist:librarySortColumn");
  const sortDirectionData = localStorage.getItem("vglist:librarySortDirection");

  if (sortColumnData) {
    sorting.value = [
      {
        id: sortColumnData,
        desc: sortDirectionData === "desc"
      }
    ];
  } else {
    // Default sort by rating descending
    sorting.value = [{ id: "rating", desc: true }];
  }
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        _createElementVNode("div", _hoisted_4, [
          _createElementVNode("div", _hoisted_5, [
            _createElementVNode("button", _hoisted_6, [
              _cache[1] || (_cache[1] = _createElementVNode("span", null, "Display columns", -1)),
              _createElementVNode("span", {
                class: "icon",
                innerHTML: __props.chevronDownIcon
              }, null, 8, _hoisted_7)
            ])
          ]),
          _createElementVNode("div", _hoisted_8, [
            _createElementVNode("div", _hoisted_9, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(table).getAllLeafColumns().filter((col) => col.getCanHide()), (column) => {
                return (_openBlock(), _createElementBlock("div", {
                  class: "dropdown-item no-link-highlight",
                  key: column.id
                }, [
                  _createElementVNode("a", {
                    onClick: ($event: any) => (column.toggleVisibility())
                  }, [
                    _createElementVNode("input", {
                      checked: column.getIsVisible(),
                      type: "checkbox"
                    }, null, 8, _hoisted_11),
                    _createTextVNode(" " + _toDisplayString(column.columnDef.header), 1)
                  ], 8, _hoisted_10)
                ]))
              }), 128))
            ])
          ])
        ]),
        (__props.isEditable)
          ? (_openBlock(), _createElementBlock("button", {
              key: 0,
              onClick: _cache[0] || (_cache[0] = ($event: any) => (addGame())),
              class: "button is-fullwidth-mobile mr-5 mr-0-mobile"
            }, " Add a game to your library "))
          : _createCommentVNode("", true)
      ])
    ]),
    _createElementVNode("table", {
      class: _normalizeClass(["table is-fullwidth is-striped library-table", { 'is-dark': isDarkMode.value }])
    }, [
      _createElementVNode("thead", null, [
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(table).getHeaderGroups(), (headerGroup) => {
          return (_openBlock(), _createElementBlock("tr", {
            key: headerGroup.id
          }, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(headerGroup.headers, (header) => {
              return (_openBlock(), _createElementBlock("th", {
                key: header.id,
                class: _normalizeClass({
              'is-sortable': header.column.getCanSort(),
              'cursor-pointer': header.column.getCanSort()
            }),
                onClick: ($event: any) => (header.column.getCanSort() ? header.column.toggleSorting() : null)
              }, [
                (!header.isPlaceholder)
                  ? (_openBlock(), _createBlock(_unref(FlexRender), {
                      key: 0,
                      render: header.column.columnDef.header,
                      props: header.getContext()
                    }, null, 8, ["render", "props"]))
                  : _createCommentVNode("", true),
                (header.column.getIsSorted())
                  ? (_openBlock(), _createElementBlock("span", _hoisted_13, _toDisplayString(header.column.getIsSorted() === "asc" ? "▲" : "▼"), 1))
                  : _createCommentVNode("", true)
              ], 10, _hoisted_12))
            }), 128))
          ]))
        }), 128))
      ]),
      _createElementVNode("tbody", null, [
        (_unref(table).getRowModel().rows.length > 0)
          ? (_openBlock(true), _createElementBlock(_Fragment, { key: 0 }, _renderList(_unref(table).getRowModel().rows, (row) => {
              return (_openBlock(), _createElementBlock("tr", {
                key: row.id
              }, [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(row.getVisibleCells(), (cell) => {
                  return (_openBlock(), _createElementBlock("td", {
                    key: cell.id
                  }, [
                    _createVNode(_unref(FlexRender), {
                      render: cell.column.columnDef.cell,
                      props: cell.getContext()
                    }, null, 8, ["render", "props"])
                  ]))
                }), 128))
              ]))
            }), 128))
          : (_openBlock(), _createElementBlock("tr", _hoisted_14, [
              _createElementVNode("td", {
                colspan: _unref(table).getAllLeafColumns().length,
                class: "has-text-centered py-20"
              }, [
                (__props.isLoading)
                  ? (_openBlock(), _createElementBlock("span", _hoisted_16, "Loading..."))
                  : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                      _cache[3] || (_cache[3] = _createElementVNode("span", { class: "has-text-grey" }, "This library is empty.", -1)),
                      (__props.isEditable)
                        ? (_openBlock(), _createElementBlock("p", _hoisted_17, [...(_cache[2] || (_cache[2] = [
                            _createTextVNode(" Want to get a headstart? ", -1),
                            _createElementVNode("a", { href: "/settings/import" }, "Connect your Steam account", -1),
                            _createTextVNode(" and import your games to get started. ", -1)
                          ]))]))
                        : _createCommentVNode("", true)
                    ], 64))
              ], 8, _hoisted_15)
            ]))
      ])
    ], 2)
  ]))
}
}

})